import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Axios from "axios";

import {
  MAINTENANCE_MODE,
  MAINTENANCE_MODE_ALLOWED_IPS,
  MAINTENANCE_MODE_COUNTRY,
  MAINTENANCE_MODE_ALLOWED_COUNTRY,
  IPINFO_API_URL,
  IPIFY_API_URL,
  ENV,
} from "@/config/appConfig";
import { logoutAction } from "@/features/auth/store/logoutAction";
import { logger } from "@/helpers/loggerHelper";

type GeoResponse = {
  country?: string;
  country_code?: string;
};

type IpResponse = {
  ip?: string;
};

type DispatchAction = ReturnType<typeof logoutAction>;

function LogoutWhenMaintenanceOn() {
  const dispatch = useDispatch() as (_action: DispatchAction) => void;
  const location = useLocation();

  // Only run check if token is present (user likely authenticated)
  const token = localStorage.getItem("token");

  // Use a clearly named key for this maintenance auto-logout logic
  const maintenanceHasLoggedOut = sessionStorage.getItem(
    "maintenanceHasLoggedOut"
  );

  const logoutUserNow = useCallback(() => {
    // Set the flag to prevent repeated API/logout loops until next login
    sessionStorage.setItem("maintenanceHasLoggedOut", "true");
    dispatch(logoutAction());
    window.location.reload();
  }, [dispatch]);

  const maintenanceOnLogout = useCallback(async () => {
    try {
      if (MAINTENANCE_MODE_COUNTRY) {
        let country = sessionStorage.getItem("userCNT");
        if (!country) {
          const geoUrl = new URL(IPINFO_API_URL);
          geoUrl.searchParams.set("token", ENV.PUBLIC_IPINFO_TOKEN);
          // Fetch geo details only if not cached
          const geoRes = await Axios.get<GeoResponse>(geoUrl.toString());
          // Most APIs return 'country', but fallback to 'country_code'
          country = geoRes.data.country || geoRes.data.country_code;
          sessionStorage.setItem("userCNT", country);
        }
        // Defensive: Use allowed list and explicit not-allowed logic
        if (!country || !MAINTENANCE_MODE_ALLOWED_COUNTRY.includes(country)) {
          logoutUserNow();
          return; // Immediately exit
        }
      } else {
        // Check if IP is already cached in sessionStorage
        let ipAdd = sessionStorage.getItem("userIP");
        if (!ipAdd) {
          // Use JSON response
          const { data } = await Axios.get<IpResponse>(IPIFY_API_URL);
          ipAdd = data.ip;
          sessionStorage.setItem("userIP", ipAdd); // Cache the IP address
        }
        // Defensive: Use allowed list logic
        if (!ipAdd || !MAINTENANCE_MODE_ALLOWED_IPS.includes(ipAdd)) {
          logoutUserNow();
          return; // Immediately exit
        }
      }
    } catch (error) {
      logger.error("Error fetching IP or country code:", { error });
      // Fail-safe: logout if unable to fetch IP or country
      logoutUserNow();
    }
  }, [logoutUserNow]);

  useEffect(() => {
    // Only check for maintenance logout if token exists (user is logged in)
    if (MAINTENANCE_MODE && token) {
      if (maintenanceHasLoggedOut) return;
      maintenanceOnLogout();
    } else if (!token) {
      // If not logged in, also clear the flag
      sessionStorage.removeItem("maintenanceHasLoggedOut");
    }
  }, [location.pathname, maintenanceOnLogout, maintenanceHasLoggedOut, token]);

  return null;
}

export default LogoutWhenMaintenanceOn;
